import {
  type EvaluationContext,
  OpenFeatureProvider as OFProvider,
  useFlag as OFUseFlag,
  OpenFeature,
  InMemoryProvider,
} from '@openfeature/react-sdk'
import type { ApiResult } from 'api/client'

type Flags = ApiResult<'get', '/api/v3/feature-flag'> | undefined

const createFlag = (flag: string) => ({
  disabled: false,
  variants: { on: true, off: false },
  defaultVariant: 'on',
  contextEvaluator: (context: EvaluationContext) =>
    (context?.flags as Flags)?.find?.((f: { name: string }) => f.name === flag)?.value
      ? 'on'
      : 'off',
})

/**
 * Declare feature flags here.
 * Do not use negative flags, where off variant is the default.
 * It will mess with the `FeatureFlag` component.
 *
 * (!) When updating it here, make sure to also update the feature flag
 * enum in @vesyl/core/feature-flag so they are in sync.
 */
const config = {
  'update-order-status': createFlag('update-order-status'),
  'vesyl-badge': createFlag('vesyl-badge'),
  'new-label-print': createFlag('new-label-print'),
  'new-batching': createFlag('new-batching'),
}

OpenFeature.setProvider(new InMemoryProvider(config))

/**
 * Set the context for feature flags (identifies the user).
 *
 * @param context - The context to set.
 */
export const setFeatureFlagContext = (context: EvaluationContext) => OpenFeature.setContext(context)

/**
 * Provider for feature flags.
 *
 * @param children - The child elements to render.
 * @returns The child elements wrapped in the feature flag provider.
 */
export const FeatureProvider = ({ children }: { children: React.ReactNode }) => {
  return <OFProvider>{children}</OFProvider>
}

/**
 * Hook to get the value of a feature flag.
 *
 * @param flag - The feature flag to get the value of.
 * @param defaultValue - The default value to return if the flag is not set.
 * @returns The value of the feature flag.
 */
export const useFlag = (flag: keyof typeof config, defaultValue: boolean) => {
  return OFUseFlag(flag, defaultValue)
}

/**
 * Component to conditionally render a child element based on a feature flag.
 *
 * @param flag - The feature flag to check.
 * @param defaultValue - The default value to use if the flag is not set.
 * @param children - The child element to render if the flag is enabled.
 * @returns The child element if the flag is enabled, otherwise null.
 */
export const FeatureFlag = ({
  flag,
  match,
  defaultValue,
  children,
}: {
  flag: keyof typeof config
  match: boolean
  defaultValue?: boolean
  children: React.ReactNode
}) => {
  const flagValue = useFlag(flag, defaultValue ?? false)
  if (flagValue?.value !== match) return null

  return <>{children}</>
}
